import { ImageOff } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { cn } from '@/Domain/utils/cn';
import { MOBILE_ADDRESS_BAR_HEIGHT, SafariBrowserFrame } from '@/UI/ChatPage/SafariBrowserFrame';
import NewWidget from '@/UI/Website/NewWidget/NewWidget';
import { WebsiteView } from '@/UI/Website/viewMode';

// Render the widget at the screenshot's 1280x720 capture viewport (screenshot.py) and CSS-scale to fit.
const WIDGET_REFERENCE_WIDTH = 1280;
const WIDGET_REFERENCE_HEIGHT = 720;
// Mobile phone-frame reference (390:844), forwarded so the widget renders the fullscreen conversation.
const MOBILE_REFERENCE_WIDTH = 390;
const MOBILE_REFERENCE_HEIGHT = 844;
// Widget's visual viewport = screen minus the overlaid address bar, so its bottom-anchored launcher/chat
// clears the bar (mirrors a real mobile browser). The screenshot behind stays full-height under the bar.
const MOBILE_WIDGET_VIEWPORT_HEIGHT = MOBILE_REFERENCE_HEIGHT - MOBILE_ADDRESS_BAR_HEIGHT;

const ProportionalWidgetLayer = ({
  width,
  height,
  children,
}: {
  width: number;
  height: number;
  children: ReactNode;
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const update = () => setScale(el.clientWidth / width);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [width]);
  return (
    <div ref={ref} className="absolute inset-0 overflow-hidden">
      <div
        className="origin-top-left"
        style={{
          width,
          height,
          transform: `scale(${scale})`,
          visibility: scale ? 'visible' : 'hidden',
        }}
      >
        {children}
      </div>
    </div>
  );
};

type WidgetPreviewProps = {
  viewMode: WebsiteView;
  deviceMode: string;
  url: string;
  customParams?: Record<string, unknown>;
  colorScheme?: 'system' | 'light' | 'dark';
  language?: string;
  headerAction?: ReactNode;
  // Bubble variant only: screenshot backdrop + editable-address/refresh chrome.
  screenshotUrl?: string;
  screenshotMobileUrl?: string;
  isScreenshotLoading?: boolean;
  screenshotFailed?: boolean;
  onUrlChange?: (url: string) => void;
  onRefresh?: () => void;
  isRefreshing?: boolean;
  // Forwarded to every NewWidget instance; see NewWidgetProps.bindContextIframe.
  bindContextIframe?: boolean;
};

// Enough product cells to fill the tall phone frame (and the 16:9 desktop mockup) with several rows.
const SKELETON_PRODUCT_CELLS = Array.from({ length: 12 }, (_, i) => i);

const WebsiteSkeleton = ({ loading, failed }: { loading?: boolean; failed?: boolean }) => (
  <div className="relative h-full w-full bg-background">
    <div className={cn('h-full w-full overflow-hidden p-5', loading && 'animate-pulse')}>
      <div className="mx-auto flex h-full w-full max-w-4xl flex-col gap-4">
        {/* Nav bar */}
        <div className="flex items-center gap-3">
          <div className="h-6 w-24 rounded bg-muted" />
          <div className="ml-auto flex gap-2">
            <div className="h-4 w-12 rounded bg-muted" />
            <div className="h-4 w-12 rounded bg-muted" />
            <div className="h-4 w-12 rounded bg-muted" />
          </div>
        </div>
        {/* Hero */}
        <div className="mt-1 h-8 w-2/3 rounded bg-muted" />
        <div className="h-4 w-1/2 rounded bg-muted" />
        {/* Product grid — several rows of image + title + price so the page reads as full content */}
        <div className="mt-1 grid grid-cols-3 gap-3">
          {SKELETON_PRODUCT_CELLS.map((cell) => (
            <div key={cell} className="flex flex-col gap-2">
              <div className="aspect-square rounded-lg bg-muted" />
              <div className="h-3 w-3/4 rounded bg-muted" />
              <div className="h-3 w-1/2 rounded bg-muted" />
            </div>
          ))}
        </div>
      </div>
    </div>
    {failed && (
      <div className="absolute inset-0 flex items-center justify-center">
        <ImageOff className="size-8 text-muted-foreground" />
      </div>
    )}
  </div>
);

export const WidgetPreview = ({
  viewMode,
  deviceMode,
  url,
  customParams,
  colorScheme,
  language,
  headerAction,
  screenshotUrl,
  screenshotMobileUrl,
  isScreenshotLoading,
  screenshotFailed,
  onUrlChange,
  onRefresh,
  isRefreshing,
  bindContextIframe,
}: WidgetPreviewProps) => {
  // Desktop pins content to a fixed 16:9 viewport; mobile fills the phone frame.
  const contentAspect = deviceMode === 'mobile' ? 'aspect-[390/844]' : 'aspect-[16/9]';
  // Reference viewport (per device) the widget renders into before CSS-scaling; matches the screenshot.
  const referenceViewport =
    deviceMode === 'mobile'
      ? { width: MOBILE_REFERENCE_WIDTH, height: MOBILE_WIDGET_VIEWPORT_HEIGHT }
      : { width: WIDGET_REFERENCE_WIDTH, height: WIDGET_REFERENCE_HEIGHT };

  if (viewMode === WebsiteView.ChatPage) {
    return (
      <SafariBrowserFrame
        url={url}
        copyValue={url}
        headerAction={headerAction}
        hideWindowControls={deviceMode === 'mobile'}
        isMobile={deviceMode === 'mobile'}
        contentAspect={contentAspect}
      >
        <ProportionalWidgetLayer width={referenceViewport.width} height={referenceViewport.height}>
          <div className="h-full w-full bg-muted">
            <NewWidget
              key={`chat-page-${deviceMode}`}
              showViewModeToggle={false}
              fixedViewType="public_link"
              source="preview"
              className="h-full"
              customParams={customParams}
              language={language}
              hostViewport={referenceViewport}
              bindContextIframe={bindContextIframe}
            />
          </div>
        </ProportionalWidgetLayer>
      </SafariBrowserFrame>
    );
  }

  if (viewMode === WebsiteView.Embed) {
    return (
      <SafariBrowserFrame
        url={url}
        headerAction={headerAction}
        hideWindowControls={deviceMode === 'mobile'}
        isMobile={deviceMode === 'mobile'}
        contentAspect={contentAspect}
      >
        <ProportionalWidgetLayer width={referenceViewport.width} height={referenceViewport.height}>
          <div className="h-full bg-muted flex flex-col overflow-hidden">
            <div className="flex-1 min-h-0 p-8">
              <div className="mx-auto h-full max-w-xl rounded-xl overflow-hidden border border-border">
                <NewWidget
                  key={`embed-${deviceMode}`}
                  showViewModeToggle={false}
                  fixedViewType="embed"
                  source="preview"
                  customParams={customParams}
                  colorScheme={colorScheme}
                  language={language}
                  hostViewport={referenceViewport}
                  bindContextIframe={bindContextIframe}
                />
              </div>
            </div>
          </div>
        </ProportionalWidgetLayer>
      </SafariBrowserFrame>
    );
  }

  const bubbleWidget = (
    <NewWidget
      key={`bubble-${deviceMode}`}
      showViewModeToggle={false}
      fixedViewType="bubble"
      source="preview"
      customParams={customParams}
      colorScheme={colorScheme}
      language={language}
      hostViewport={referenceViewport}
      bindContextIframe={bindContextIframe}
    />
  );

  // Mobile uses the vertical screenshot (falls back to desktop if missing); desktop uses the 16:9.
  const bubbleScreenshot =
    deviceMode === 'mobile' ? (screenshotMobileUrl ?? screenshotUrl) : screenshotUrl;

  return (
    <SafariBrowserFrame
      url={url}
      headerAction={headerAction}
      hideWindowControls={deviceMode === 'mobile'}
      isMobile={deviceMode === 'mobile'}
      contentAspect={contentAspect}
      onUrlChange={onUrlChange}
      onRefresh={onRefresh}
      isRefreshing={isRefreshing}
    >
      <div className="h-full bg-background relative overflow-hidden">
        {bubbleScreenshot ? (
          <img
            src={bubbleScreenshot}
            alt="Website preview"
            // Slight blur so the widget reads as the sharp focal point.
            className="h-full w-full object-cover object-top blur-[2px]"
          />
        ) : (
          <WebsiteSkeleton loading={isScreenshotLoading} failed={screenshotFailed} />
        )}
        {bubbleScreenshot && (
          // Scrim dims the blurred screenshot so the widget pops; on mobile it stops above the address bar so the bar's frosted glass isn't washed flat.
          <div
            className="pointer-events-none absolute inset-x-0 top-0 bg-white/50 dark:bg-black/50"
            style={{ bottom: deviceMode === 'mobile' ? MOBILE_ADDRESS_BAR_HEIGHT : 0 }}
          />
        )}
        <ProportionalWidgetLayer width={referenceViewport.width} height={referenceViewport.height}>
          {bubbleWidget}
        </ProportionalWidgetLayer>
      </div>
    </SafariBrowserFrame>
  );
};
